import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Signup.css';
import { 
  User, 
  Mail, 
  Lock, 
  Eye, 
  EyeOff,
  Check,
  X,
  ChevronRight,
  ChevronLeft,
  Loader2,
  AtSign,
  Bell,
  Shield
} from "lucide-react";

function Signup() {
  const navi = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
    newsletter: true
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    
    // Extract first and last name from full name
    const nameParts = formData.name.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (firstName.length < 1 || lastName.length < 1) {
      newErrors.name = 'Please enter both first and last name';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    return newErrors;
  };

  const validateStep2 = () => {
    const newErrors = {};
    
    if (!formData.username) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, and underscores';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    return newErrors;
  };

  const handleNextStep = () => {
    if (currentStep === 1) {
      const step1Errors = validateStep1();
      if (Object.keys(step1Errors).length > 0) {
        setErrors(step1Errors);
        return;
      }
      setCurrentStep(2);
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(1);
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (currentStep === 1) {
      handleNextStep();
      return;
    }
    
    const step2Errors = validateStep2();
    if (Object.keys(step2Errors).length > 0) {
      setErrors(step2Errors);
      return;
    }
    
    setIsLoading(true);
    try {
      console.log("Sending data to /registerUser:", formData);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/registerUser`,
        formData,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log("Response:", response);
      if (response.status === 200) {
        sessionStorage.setItem("username", formData.username);
        navi("/");
      }
    } catch (error) {
      console.error("Register Error:", error);
      
      if (error.response) {
        alert(`Registration Failed: ${error.response.data.error || 'Unknown error'}`);
      } else if (error.request) {
        alert("No response from server. Please check your connection.");
      } else {
        alert("Registration failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    window.location.href = `${process.env.REACT_APP_FILE_URL}/auth/google`;
  };

  const checkUsernameAvailability = async () => {
    if (!formData.username || formData.username.length < 3) return;
    
    // Simulate username check
    const takenUsernames = ['admin', 'user', 'test', 'demo'];
    if (takenUsernames.includes(formData.username.toLowerCase())) {
      setErrors(prev => ({ ...prev, username: 'Username is already taken' }));
    } else {
      setErrors(prev => ({ ...prev, username: '' }));
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { score: 0, label: 'None', color: '#4b5563' };
    
    let score = 0;
    if (password.length >= 6) score++;
    if (password.length >= 8) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[!@#$%^&*]/.test(password)) score++;
    
    const strengths = [
      { score: 1, label: 'Weak', color: '#ef4444' },
      { score: 2, label: 'Fair', color: '#f59e0b' },
      { score: 3, label: 'Good', color: '#10b981' },
      { score: 4, label: 'Strong', color: '#10b981' },
      { score: 5, label: 'Very Strong', color: '#10b981' }
    ];
    
    return strengths[Math.min(score - 1, 4)] || strengths[0];
  };

  const passwordStrength = getPasswordStrength(formData.password);

  // Split full name into first and last name for display
  const getDisplayName = () => {
    if (!formData.name) return { firstName: '', lastName: '' };
    const nameParts = formData.name.trim().split(' ');
    return {
      firstName: nameParts[0] || '',
      lastName: nameParts.slice(1).join(' ') || ''
    };
  };

  const displayName = getDisplayName();

  return (
    <div className="signup-container">
      {/* Background Elements */}
      <div className="signup-bg">
        <div className="signup-gradient"></div>
        <div className="signup-pattern"></div>
      </div>

      {/* Main Content */}
      <div className="signup-content">
        {/* Header */}
        <div className="signup-header">
          <img src="/UniCircle.png" alt="UniCircle" className="signup-logo" />
          <h1 className="signup-title">Join UniCircle today</h1>
          <p className="signup-subtitle">Create your account — it's free!</p>
        </div>

        {/* Progress Bar */}
        <div className="signup-progress">
          <div className="progress-steps">
            <div className={`progress-step ${currentStep >= 1 ? 'active' : ''}`}>
              <User size={16} />
            </div>
            <div className={`progress-line ${currentStep >= 2 ? 'active' : ''}`} />
            <div className={`progress-step ${currentStep >= 2 ? 'active' : ''}`}>
              <Lock size={16} />
            </div>
          </div>
          <span className="progress-text">Step {currentStep} of 2</span>
        </div>

        {/* Signup Card */}
        <div className="signup-card">
          <form onSubmit={handleSubmit} className="signup-form">
            {/* Step 1: Personal Information */}
            {currentStep === 1 && (
              <div className="step-content">
                {/* Name Fields */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First name</label>
                    <div className="input-wrapper">
                      <User size={18} className="input-icon" />
                      <input
                        type="text"
                        id="firstName"
                        value={displayName.firstName}
                        onChange={(e) => {
                          const newName = e.target.value + ' ' + displayName.lastName;
                          setFormData(prev => ({ ...prev, name: newName.trim() }));
                        }}
                        className={`form-input ${errors.name ? 'error' : ''}`}
                        placeholder="John"
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="lastName">Last name</label>
                    <div className="input-wrapper">
                      <User size={18} className="input-icon" />
                      <input
                        type="text"
                        id="lastName"
                        value={displayName.lastName}
                        onChange={(e) => {
                          const newName = displayName.firstName + ' ' + e.target.value;
                          setFormData(prev => ({ ...prev, name: newName.trim() }));
                        }}
                        className={`form-input ${errors.name ? 'error' : ''}`}
                        placeholder="Doe"
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                </div>
                {errors.name && <div className="error-message">{errors.name}</div>}

                {/* Email Field */}
                <div className="form-group">
                  <label htmlFor="email">Email address</label>
                  <div className="input-wrapper">
                    <Mail size={18} className="input-icon" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`form-input ${errors.email ? 'error' : ''}`}
                      placeholder="you@example.com"
                      disabled={isLoading}
                    />
                    {formData.email && !errors.email && 
                      /\S+@\S+\.\S+/.test(formData.email) && (
                      <Check size={16} className="input-check" />
                    )}
                  </div>
                  {errors.email && <div className="error-message">{errors.email}</div>}
                </div>

                {/* Continue Button */}
                <button
                  type="button"
                  onClick={handleNextStep}
                  className="signup-button primary"
                  disabled={isLoading}
                >
                  <span>Continue</span>
                  <ChevronRight size={18} />
                </button>

                {/* Divider */}
                <div className="signup-divider">
                  <span>or</span>
                </div>

                {/* Google Signup */}
                {/* <button
                  type="button"
                  className="signup-button google"
                  onClick={handleGoogleSignup}
                  disabled={isLoading}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span>Join with Google</span>
                </button> */}
              </div>
            )}

            {/* Step 2: Account Setup */}
            {currentStep === 2 && (
              <div className="step-content">
                {/* Back Button */}
                <button 
                  type="button"
                  onClick={handlePrevStep}
                  className="back-button"
                  disabled={isLoading}
                >
                  <ChevronLeft size={16} />
                  <span>Back</span>
                </button>

                {/* Username Field */}
                <div className="form-group">
                  <label htmlFor="username">Username</label>
                  <div className="input-wrapper">
                    <AtSign size={18} className="input-icon" />
                    <input
                      type="text"
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      onBlur={checkUsernameAvailability}
                      className={`form-input ${errors.username ? 'error' : ''}`}
                      placeholder="johndoe"
                      disabled={isLoading}
                    />
                    {formData.username && formData.username.length >= 3 && !errors.username && (
                      <Check size={16} className="input-check success" />
                    )}
                    {errors.username && (
                      <X size={16} className="input-check error" />
                    )}
                  </div>
                  {errors.username ? (
                    <div className="error-message">{errors.username}</div>
                  ) : formData.username && formData.username.length >= 3 ? (
                    <div className="success-message">✓ Username available</div>
                  ) : null}
                </div>

                {/* Password Field */}
                <div className="form-group">
                  <label htmlFor="password">Password (min. 6 characters)</label>
                  <div className="input-wrapper">
                    <Lock size={18} className="input-icon" />
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className={`form-input ${errors.password ? 'error' : ''}`}
                      placeholder="••••••••"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isLoading}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  
                  {/* Password Strength Meter */}
                  {formData.password && (
                    <div className="password-strength">
                      <div className="strength-meter">
                        <div 
                          className={`strength-bar ${passwordStrength.label.toLowerCase()}`}
                          style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                        />
                      </div>
                      <span className="strength-label" style={{ color: passwordStrength.color }}>
                        {passwordStrength.label}
                      </span>
                    </div>
                  )}
                  {errors.password && <div className="error-message">{errors.password}</div>}
                </div>

                {/* Confirm Password Field */}
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm password</label>
                  <div className="input-wrapper">
                    <Lock size={18} className="input-icon" />
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                      placeholder="••••••••"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={isLoading}
                    >
                      {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.confirmPassword && <div className="error-message">{errors.confirmPassword}</div>}
                  {formData.password && formData.confirmPassword && formData.password === formData.confirmPassword && (
                    <div className="success-message">✓ Passwords match</div>
                  )}
                </div>

                {/* Terms Checkbox */}
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="agreeToTerms"
                      checked={formData.agreeToTerms}
                      onChange={handleChange}
                      className="checkbox-input"
                      disabled={isLoading}
                    />
                    <span className="checkbox-text">
                      I agree to the{' '}
                      <Link to="/terms" className="terms-link">
                        Terms of Service
                      </Link>
                      {' '}and{' '}
                      <Link to="/privacy" className="terms-link">
                        Privacy Policy
                      </Link>
                    </span>
                  </label>
                  {errors.agreeToTerms && <div className="error-message">{errors.agreeToTerms}</div>}
                </div>

                {/* Newsletter Checkbox */}
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="newsletter"
                      checked={formData.newsletter}
                      onChange={handleChange}
                      className="checkbox-input"
                      disabled={isLoading}
                    />
                    <span className="checkbox-text">
                      <Bell size={14} />
                      Send me product updates, tips, and inspiration
                    </span>
                  </label>
                </div>

                {/* Submit Button */}
                <button 
                  type="submit" 
                  className="signup-button primary"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={18} className="spin" />
                      <span>Creating account...</span>
                    </>
                  ) : (
                    <>
                      <span>Create account</span>
                      <Shield size={18} />
                    </>
                  )}
                </button>
              </div>
            )}
          </form>

          {/* Sign In Link */}
          <div className="signin-prompt">
            <p>
              Already have an account?{' '}
              <Link to="/" className="signin-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>

        {/* Footer Links */}
        <footer className="signup-footer">
          <Link to="/terms">Terms</Link>
          <span>•</span>
          <Link to="/privacy">Privacy</Link>
          <span>•</span>
          <Link to="/help">Help</Link>
        </footer>
      </div>
    </div>
  );
}

export default Signup;